import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StatusBadge } from '@/components/StatusBadge';
import { ImagePreview } from '@/components/ImagePreview';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Eye, Edit, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { Member } from '@/types';
import {
  getLifeStatus,
  getMembershipStatus,
} from '@/lib/utils';

export type SortField = 'id' | 'name' | 'email' | 'phone' | 'joinedDate' | 'endDate';
export type SortDirection = 'asc' | 'desc';

export interface ColumnSort {
  field: SortField;
  direction: SortDirection;
}

export interface TableColumn {
  id: string;
  label: string;
  sortable: boolean;
  renderHeader: (sort: ColumnSort | null, onSort: (field: SortField) => void) => React.ReactNode;
  renderCell: (member: Member) => React.ReactNode;
  className?: string;
}

const ACTION_BUTTON_CLASS =
  'h-8 w-8 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]';

const HeaderLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <span
    className={`text-xs font-semibold tracking-wide text-gray-800 ${className ?? ''}`}
  >
    {children}
  </span>
);

const SortButton: React.FC<{
  field: SortField;
  currentSort: ColumnSort | null;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ field, currentSort, onSort, children, className }) => {
  const isActive = currentSort?.field === field;
  const isAsc = currentSort?.direction === 'asc';

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`-ml-2 h-8 gap-1 px-2 text-xs font-semibold tracking-wide hover:bg-gray-100 ${
        isActive ? 'text-gray-900' : 'text-gray-800 hover:text-gray-900'
      } ${className ?? ''}`}
      onClick={() => onSort(field)}
    >
      {children}
      {isActive ? (
        isAsc ? (
          <ArrowUp className="h-3.5 w-3.5 text-[#4369a0]" />
        ) : (
          <ArrowDown className="h-3.5 w-3.5 text-[#4369a0]" />
        )
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
      )}
    </Button>
  );
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '--';
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const displayValue = (value: string | null | undefined): string => {
  return value && value.trim() !== '' ? value : '--';
};


export const createColumns = (
  sort: ColumnSort | null,
  onSort: (field: SortField) => void,
  onView: (member: Member) => void,
  onEdit: (member: Member) => void,
  getMonthsPaid: (memberId: string) => number
): TableColumn[] => [
  {
    id: 'id',
    label: 'ID',
    sortable: true,
    className: 'w-[72px] text-center',
    renderHeader: () => (
      <div className="flex items-center justify-center">
        <SortButton field="id" currentSort={sort} onSort={onSort} className="mx-auto">
          ID
        </SortButton>
      </div>
    ),
    renderCell: (member) => (
      <div className="text-center text-sm font-medium tabular-nums text-gray-500">{member.id}</div>
    ),
  },
  {
    id: 'member',
    label: 'Member',
    sortable: true,
    className: 'w-[240px] max-w-[250px] pr-2',
    renderHeader: () => (
      <SortButton field="name" currentSort={sort} onSort={onSort}>
        Member
      </SortButton>
    ),
    renderCell: (member) => (
      <div className="flex min-w-0 items-center gap-2.5">
        <ImagePreview src={member.profileImage || member.photo} alt={member.name || 'Member photo'}>
        <Avatar className="h-9 w-9 shrink-0 border border-white shadow-sm ring-1 ring-gray-200/60">
          <AvatarImage src={member.profileImage || member.photo} />
          <AvatarFallback className="bg-[#4369a0]/10 text-xs font-semibold text-[#4369a0]">
            {member.name
              ? member.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
              : '--'}
          </AvatarFallback>
        </Avatar>
        </ImagePreview>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onView(member)}
            className="block w-full cursor-pointer truncate text-left text-sm font-medium text-gray-900 hover:text-[#4369a0] hover:underline"
          >
            {displayValue(member.name)}
          </button>
          <div className="w-full truncate text-xs text-gray-500">{displayValue(member.email)}</div>
        </div>
      </div>
    ),
  },
  {
    id: 'lifeStatus',
    label: 'Life Status',
    sortable: false,
    className: 'w-[110px]',
    renderHeader: () => <HeaderLabel>Life Status</HeaderLabel>,
    renderCell: (member) => {
      return <StatusBadge status={getLifeStatus(member)} />;
    },
  },
  {
    id: 'phone',
    label: 'Phone',
    sortable: true,
    className: 'w-[130px] pl-1 whitespace-nowrap',
    renderHeader: () => (
      <SortButton field="phone" currentSort={sort} onSort={onSort}>
        Phone
      </SortButton>
    ),
    renderCell: (member) => (
      <div className="text-sm text-gray-700">{displayValue(member.phone)}</div>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    sortable: false,
    className: 'w-[100px]',
    renderHeader: () => <HeaderLabel>Status</HeaderLabel>,
    renderCell: (member) => {
      return (
        <StatusBadge status={getMembershipStatus(member, getMonthsPaid(member.id))} />
      );
    },
  },
  {
    id: 'joinedDate',
    label: 'Joined',
    sortable: true,
    className: 'min-w-[120px]',
    renderHeader: () => (
      <SortButton field="joinedDate" currentSort={sort} onSort={onSort}>
        Joined
      </SortButton>
    ),
    renderCell: (member) => (
      <div className="text-sm text-gray-600">{formatDate(member.joinedDate)}</div>
    ),
  },
  {
    id: 'endDate',
    label: 'Expires',
    sortable: true,
    className: 'min-w-[120px]',
    renderHeader: () => (
      <SortButton field="endDate" currentSort={sort} onSort={onSort}>
        Expires
      </SortButton>
    ),
    renderCell: (member) => (
      <div className="text-sm text-gray-600">{formatDate(member.endDate)}</div>
    ),
  },
  {
    id: 'address',
    label: 'Address',
    sortable: false,
    className: 'min-w-[180px] max-w-[220px]',
    renderHeader: () => <HeaderLabel>Address</HeaderLabel>,
    renderCell: (member) => (
      <div className="truncate text-sm text-gray-600" title={member.address || '--'}>
        {displayValue(member.address)}
      </div>
    ),
  },
  {
    id: 'actions',
    label: 'Actions',
    sortable: false,
    className: 'w-[108px] text-center',
    renderHeader: () => <HeaderLabel className="block text-center">Actions</HeaderLabel>,
    renderCell: (member) => (
      <div className="flex items-center justify-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onView(member)}
              className={ACTION_BUTTON_CLASS}
            >
              <Eye className="h-3.5 w-3.5" />
              <span className="sr-only">View member</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">View</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(member)}
              className={ACTION_BUTTON_CLASS}
            >
              <Edit className="h-3.5 w-3.5" />
              <span className="sr-only">Edit member</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Edit</TooltipContent>
        </Tooltip>
      </div>
    ),
  },
];
